import type {
  ApprovalTemplate,
  ApprovalRouteStepInput,
} from "../api/generated";
import { isApiClientError } from "../api/errors";
import type { ApprovalTemplatePatch } from "../entities/document-workflow/approvals";

export interface ApprovalLevelDraft {
  key: string;
  mode: ApprovalRouteStepInput["mode"];
  amount: string;
  unit: "working_days" | "hours";
  approver_ids: string[];
}

export const newApprovalLevel = (): ApprovalLevelDraft => ({
  key: crypto.randomUUID(),
  mode: "all",
  amount: "",
  unit: "working_days",
  approver_ids: [],
});

export function approvalLevelErrors(
  levels: readonly ApprovalLevelDraft[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (levels.length === 0) errors.steps = "Добавьте хотя бы один уровень";
  if (levels.length > 10) errors.steps = "Максимум 10 уровней";
  levels.forEach((level, index) => {
    if (level.approver_ids.length === 0)
      errors[`steps.${index}.approver_ids`] =
        "На уровне нужен хотя бы один согласующий";
    else if (new Set(level.approver_ids).size !== level.approver_ids.length)
      errors[`steps.${index}.approver_ids`] =
        "Один сотрудник не может быть дважды на уровне";
    else if (level.approver_ids.length > 10)
      errors[`steps.${index}.approver_ids`] =
        "Максимум 10 согласующих на уровне";
    if (
      level.amount !== "" &&
      (!Number.isInteger(Number(level.amount)) || Number(level.amount) <= 0)
    )
      errors[`steps.${index}.due.amount`] =
        "Срок должен быть положительным числом";
    else if (Number(level.amount) > 32767)
      errors[`steps.${index}.due.amount`] = "Срок не может превышать 32767";
  });
  return errors;
}

export function approvalLevelInputs(
  levels: readonly ApprovalLevelDraft[],
): ApprovalRouteStepInput[] {
  return levels.map((level) => ({
    mode: level.mode,
    approver_ids: level.approver_ids,
    due:
      level.amount === ""
        ? null
        : { amount: Number(level.amount), unit: level.unit },
  }));
}

export const approvalTemplateKeys = [
  "document-workflow",
  "approval-templates",
] as const;

export function approvalTemplateError(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403) return "Недостаточно прав";
    if (error.status === 404)
      return "Шаблон не найден или недоступен в этой организации";
    if (error.code === "document_workflow.approval_steps_invalid")
      return "Проверьте уровни согласования";
    if (error.code === "document_workflow.approval_approver_invalid")
      return "Выбранный согласующий недоступен или не имеет права согласования. Выберите другого сотрудника.";
  }
  return "Не удалось сохранить шаблон. Повторите попытку.";
}

export function templatePatch(
  template: ApprovalTemplate,
): ApprovalTemplatePatch {
  return {
    name: template.name,
    description: template.description,
    document_type: template.document_type,
    internal_kind: template.internal_kind,
    is_active: template.is_active,
    steps: template.steps.map((step) => ({
      mode: step.mode,
      due: step.due,
      approver_ids: step.approvers.map((person) => person.id),
    })),
  };
}
